import { useCallback, useEffect, useState } from "react";

/**
 * Convites de equipe + seats da org (epico 3). Mostrado no topo da aba
 * "Usuários" da administração.
 */
interface InviteRow {
  id: string;
  email: string;
  role: "admin" | "user";
  invitedByEmail: string;
  createdAt: string;
  expiresAt: string;
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
  } catch {
    return iso;
  }
}

interface Props {
  /** Sysadmin só - convida pra org escolhida no seletor em vez da própria. */
  orgId?: string;
}

export function InvitesPanel({ orgId }: Props) {
  const [invites, setInvites] = useState<InviteRow[]>([]);
  // limit `null` = ILIMITADO (plano sem teto de assentos). Antes o backend mandava
  // Infinity, que virava `null` no JSON e era lido como 0 -> "limite atingido" num
  // plano ilimitado ("1/ assentos" bloqueando convite). Agora null = sem limite.
  const [seats, setSeats] = useState<{ count: number; limit: number | null } | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"admin" | "user">("user");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastLink, setLastLink] = useState<string | null>(null);

  const qs = orgId ? `?orgId=${encodeURIComponent(orgId)}` : "";

  const load = useCallback(async () => {
    // /api/org é sempre a org ATIVA de quem chama (domínio/cookie) - não
    // segue o seletor de org do sysadmin, então os assentos exibidos aqui
    // podem não bater com a org escolhida (limitação conhecida).
    const [invR, orgR] = await Promise.all([fetch(`/api/admin/invites${qs}`), fetch("/api/org")]);
    if (invR.ok) setInvites((await invR.json()).items ?? []);
    if (orgR.ok) setSeats((await orgR.json()).seats ?? null);
  }, [qs]);

  useEffect(() => {
    void load();
  }, [load]);

  const invite = async () => {
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    setLastLink(null);
    try {
      const r = await fetch(`/api/admin/invites${qs}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: email.trim(), role }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(j.error ?? "Falha ao convidar.");
        return;
      }
      setEmail("");
      if (!j.delivered) setLastLink(j.link); // sem webhook pra entregar: admin copia
      await load();
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    setBusy(true);
    try {
      await fetch(`/api/admin/invites/${id}${qs}`, { method: "DELETE" });
      await load();
    } finally {
      setBusy(false);
    }
  };

  // ilimitado (limit null) nunca lota
  const seatsFull = seats && seats.limit != null ? seats.count >= seats.limit : false;
  const seatsLabel = seats
    ? seats.limit == null
      ? `${seats.count} ${seats.count === 1 ? "assento" : "assentos"} · ilimitado`
      : `${seats.count}/${seats.limit} assentos`
    : "";

  return (
    <div className="admin-block">
      <h2>
        Convidar pessoas {seats && <span className="admin-user-meta">({seatsLabel})</span>}
      </h2>

      {seatsFull && (
        <div className="auth-error" style={{ marginBottom: 10 }}>
          Limite de assentos atingido - remova alguém ou aumente o limite antes de convidar.
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
        <label className="field" style={{ flex: "1 1 220px" }}>
          <span>E-mail</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="pessoa@empresa.com"
            spellCheck={false}
          />
        </label>
        <label className="field" style={{ width: 120 }}>
          <span>Papel</span>
          <select value={role} onChange={(e) => setRole(e.target.value as "admin" | "user")}>
            <option value="user">Usuário</option>
            <option value="admin">Admin</option>
          </select>
        </label>
        <button className="btn-approve" disabled={busy || seatsFull} onClick={() => void invite()} type="button">
          Convidar
        </button>
      </div>

      {error && <div className="auth-error" style={{ marginBottom: 10 }}>{error}</div>}
      {lastLink && (
        <div className="admin-user-meta" style={{ marginBottom: 10, wordBreak: "break-all" }}>
          Sem webhook cadastrado pra essa pessoa - copie e envie o link manualmente:{" "}
          <a href={lastLink}>{lastLink}</a>
        </div>
      )}

      {invites.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {invites.map((i) => (
            <div key={i.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span className="badge">{i.role}</span>
              <strong>{i.email}</strong>
              <span className="admin-user-meta">convidado por {i.invitedByEmail} · expira {fmtDate(i.expiresAt)}</span>
              <button className="btn-reject" style={{ marginLeft: "auto" }} disabled={busy} onClick={() => void revoke(i.id)} type="button">
                Revogar
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
